import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ConfirmDialogComponent } from 'src/app/services/dialog/confirm-dialog/confirm-dialog.component';
import { RecipientMaintenanceService } from '../recipient-maintenance.service';
import { PATTERN_EMAIL, PATTERN_NUMERIC } from 'src/app/utils/constant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DialogService,
  HelperService,
  LanguageService
} from 'src/app/services';
import { environment } from 'src/environments/environment';
import { MediaObserver } from '@angular/flex-layout';
import { finalize } from 'rxjs/operators';
import * as _ from 'lodash';

import {
  RECIPIENT_DOCTOR,
  RECIPIENT_BRANCH,
  RECIPIENT_SOCIAL
} from 'src/app/utils/constant';

@Component({
  selector: 'esc-recipient-maintenance-form',
  templateUrl: './recipient-maintenance-form.component.html',
  styleUrls: ['./recipient-maintenance-form.component.scss']
})
export class RecipientMaintenanceFormComponent implements OnInit {
  @Input() detail;
  @Input() mode;
  @Input() recipientTypes;
  @Output() saving = new EventEmitter();
  @Output() closeForm = new EventEmitter();

  public readonly RECIPIENT_DOCTOR = RECIPIENT_DOCTOR;
  public readonly RECIPIENT_BRANCH = RECIPIENT_BRANCH;
  public readonly RECIPIENT_SOCIAL = RECIPIENT_SOCIAL;
  private suppressRecipientTypeChange = false; // used to dynamically trigger setting of fullname to empty string when the recipient_type changes
  private errorCodes = {
    MAX_REGISTRATION_CANNOT_BE_LESS_THAN_CURRENT_PATIENT_COUNT: 'E(240xR001)'
  }

  // Track the selected branch if the recipient type is branch
  public selectedBranch = null;

  loading = {
    submit: false
  };

  constructor(
    private fb: FormBuilder,
    public helper: HelperService,
    public media: MediaObserver,
    private dialogService: DialogService,
    public languageService: LanguageService,
    private rcpMntService: RecipientMaintenanceService
  ) {}

  public formGroup: FormGroup;

  ngOnInit() {
    this.initForm();

    const [code, description] = _.get(this.detail, 'fullname').split(' - ');
    const branchObj = {
      branch: {
        code: code,
        description: description
      }
    };
    this.selectedBranch = branchObj;
  }

  initForm() {
    const max_registration = !(
      _.isNil(_.get(this.detail, 'max_registration')) ||
      _.get(this.detail, 'max_registration') === '' ||
      _.get(this.detail, 'max_registration') === -1
    )
      ? _.get(this.detail, 'max_registration')
      : null;

    const max_registration_infinite = !(
      _.isNil(_.get(this.detail, 'max_registration')) ||
      _.get(this.detail, 'max_registration') === '' ||
      _.get(this.detail, 'max_registration') === -1
    )
      ? false
      : true;

    this.formGroup = this.fb.group({
      code: [_.get(this.detail, 'code')],

      recipient_type: [
        _.has(this.detail, 'recipient_type.code')
          ? _.get(this.detail, 'recipient_type.code')
          : this.recipientTypes[0].code,
        [Validators.required]
      ],

      fullname: [
        _.get(this.detail, 'fullname'),
        [Validators.required, Validators.maxLength(250)]
      ],

      reference_code: [
        _.get(this.detail, 'reference_code'),
        [Validators.required]
      ],

      email_address: [
        _.get(this.detail, 'email_address'),
        [Validators.pattern(PATTERN_EMAIL)]
      ],

      contact_number: [
        _.get(this.detail, 'contact_number'),
        [Validators.pattern(PATTERN_NUMERIC)]
      ],

      max_registration: [
        max_registration,
        [
          Validators.required,
          Validators.min(1),
          this.helper.validateNumbersOnly
        ]
      ],
      max_registration_infinite: [max_registration_infinite],

      status: _.get(this.detail, 'is_active') == 1 ? true : false
    });

    this.formGroup.get('recipient_type').valueChanges.subscribe(() => {
      if (!this.suppressRecipientTypeChange && !this.loading.submit) {
        this.formGroup.get('fullname').setValue('', { emitEvent: false });
        this.selectedBranch = {};
      }
    });

    if (this.formGroup.get('max_registration_infinite').value) {
      this.formGroup.get('max_registration').disable();
    } else {
      this.formGroup.get('max_registration').enable();
    }

    this.formGroup
      .get('max_registration_infinite')
      .valueChanges.subscribe((checked: boolean) => {
        if (checked) {
          this.formGroup.get('max_registration').disable();
          this.formGroup.get('max_registration').setValue(null);
        } else {
          this.formGroup.get('max_registration').enable();
        }
      });

    this.toggleDisable();
  }

  toggleDisable() {
    if (!_.get(this.detail, 'id')) {
      this.formGroup.get('status').setValue(true);
      this.formGroup.get('status').disable({ emitEvent: false });
    }
    this.formGroup.get('code').disable({ emitEvent: false });
  }

  isFormValid() {
    if (this.formGroup.valid) {
      this.saveChanges();
    } else {
      for (let ctrl in this.formGroup.controls) {
        if (!this.formGroup.controls[ctrl].valid) {
          this[ctrl + 'Required'] = true;
        }
      }
    }
  }

  saveChanges() {
    this.suppressRecipientTypeChange = true;
    this.loading.submit = true;
    this.formGroup.disable();

    let is_active = _.get(this.formGroup.get('status'), 'value') ? 1 : 0;

    let obj = {
      data: [
        {
          id: _.get(this.detail, 'id') || 0,

          //send null if adding new
          code: _.get(this.formGroup.get('code'), 'value')
            ? _.toUpper(_.get(this.formGroup.get('code'), 'value'))
            : null,

          type: _.get(this.formGroup.get('recipient_type'), 'value'),

          fullname: _.toUpper(_.get(this.formGroup.get('fullname'), 'value')),

          reference_code: _.toUpper(
            _.get(this.formGroup.get('reference_code'), 'value')
          ),

          email_address: _.get(this.formGroup.get('email_address'), 'value'),

          contact_number: _.get(this.formGroup.get('contact_number'), 'value')
            ? _.toInteger(_.get(this.formGroup.get('contact_number'), 'value'))
            : null,

          max_registration: !(
            _.isNil(this.formGroup.get('max_registration')?.value) ||
            this.formGroup.get('max_registration')?.value === ''
          )
            ? _.toInteger(this.formGroup.get('max_registration')?.value)
            : -1,

          is_active: is_active
        }
      ]
    };

    this.saving.emit(true);

    this.rcpMntService
      .insert(obj)
      .pipe(
        finalize(() => {
          this.loading.submit = false;
          this.saving.emit(false);
          this.formGroup.enable();
          this.toggleDisable();

          this.suppressRecipientTypeChange = false;
        })
      )
      .subscribe(
        (res: any) => {
          this.loading.submit = false;
          if (environment.showLogs) {
            console.log(res);
          }

          if (_.has(res, 'status') && _.get(res, 'status') == 0) {
            this.helper.networkErrorMessage();
            return;
          }

          if (this.helper.displayErrorMessage(res)) return;

          if (_.get(res, 'error.count') > 0) {
            if (!_.get(this.detail, 'id')) {
              obj.data[0].id = _.get(
                res.error.results[0],
                'details.object.id'
              );
              obj.data[0].code = _.get(
                res.error.results[0],
                'details.object.code'
              );
            }

            if (res.error.results[0].code === this.errorCodes.MAX_REGISTRATION_CANNOT_BE_LESS_THAN_CURRENT_PATIENT_COUNT) {
              this.dialogService.openSnackBar(
                this.languageService.getTranslation('MSG_ERROR_MAX_REGISTRATION_CANNOT_BE_LESS_THAN_CURRENT_PATIENT_COUNT', {})
              )
            } else {
              this.helper.networkErrorMessage();
            }
          }

          if (_.get(res, 'success.count') > 0) {
            if (!_.get(this.detail, 'id')) {
              obj.data[0].id = _.get(
                res.success.results[0],
                'details.object.id'
              );
              obj.data[0].code = _.get(
                res.success.results[0],
                'details.object.code'
              );
            }

            const recipientType =
              res.success.results[0].details.object.recipient_type;
            const clone_obj = _.clone(obj.data[0]);

            this.saving.emit([{ ...clone_obj, recipient_type: recipientType }]);
            this.dialogService.openSnackBar(
              this.languageService.getTranslation('MSG_DATA_UPDATED', {})
            );
            this.close(true);
          }
        },
        (err) => {
          console.log(err);
        }
      );
  }

  disableSaveBtn() {
    return (
      this.loading.submit || (this.formGroup.touched && !this.formGroup.valid)
    );
  }

  confirmChanges() {
    if (this.formGroup.touched && this.formGroup.valid) {
      this.dialogService
        .openDialog(ConfirmDialogComponent, {
          width: '400px',
          autoFocus: true,
          disableClose: true,
          data: {
            title: this.languageService.getTranslation(
              'MSG_CONFIRM_ACTION',
              {}
            ),
            message: this.languageService.getTranslation(
              'MSG_SAVE_CHANGES',
              {}
            ),
            button: this.languageService.getTranslation('LABEL_YES', {}),
            cancelButton: this.languageService.getTranslation('LABEL_NO', {})
          }
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            this.saveChanges();
          } else {
            this.close(false);
          }
        });
    } else {
      this.close(false);
    }
  }

  close(data) {
    this.closeForm.next(data);
  }

  branchSelected(ev) {
    this.formGroup.get('fullname').setValue(`${ev.code} - ${ev.description}`);
  }
}
